import { useAuth } from '@/context/auth-context';
import { useMemo } from 'react';
import { componentLogger, systemLogger } from '@/lib/logging';
import { UserContext, UserRole } from '@/lib/permissions/types';

/**
 * Hook to get user context with JWT claims
 * This will extract the custom claims set by the auth hook
 */
/**
 * Map database role values to permission system role values
 */
const mapAppRole = (databaseRole: string): UserRole => {
  switch (databaseRole) {
    case 'ADMIN':
      return UserRole.ADMIN; // Map database ADMIN to permission system ADMIN
    case 'COMPANY_ADMIN':
      return UserRole.COMPANY_ADMIN;
    case 'USER':
    default:
      return UserRole.USER;
  }
};

export const useUserContext = (): UserContext | null => {
  const { user, session } = useAuth();

  return useMemo(() => {
    if (!user) {
      return null;
    }

    // Use the session directly from the auth context
    try {
      if (session?.access_token) {
        // Decode JWT to get custom claims
        const tokenParts = session.access_token.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Invalid JWT token format');
        }
        const payloadString = atob(tokenParts[1] as string);
        const payload = JSON.parse(payloadString) as Record<string, unknown>;

        // DEBUG: Log JWT payload contents
        systemLogger.debug('[JWT-DEBUG] JWT payload contents:', {
          userId: user.id,
          email: user.email,
          payloadKeys: Object.keys(payload),
          app_role: payload['app_role'],
          company_id: payload['company_id'],
          company_type: payload['company_type'],
          is_system_admin: payload['is_system_admin'],
          fullPayload: payload
        });

        const appRole = mapAppRole((payload['app_role'] as string) || 'USER');
        const companyId = payload['company_id'] as string | undefined;
        const companyType = payload['company_type'] as 'CLIENT' | 'FACILITATOR' | 'WORKSHOP' | undefined;

        const contextData: UserContext = {
          id: user.id,
          app_role: appRole,
          company_type: companyType || null,  // ADD: Root level for permission checks
          profile: {
            role: appRole,
            company_id: companyId || null,
            company: companyId && companyType ? {
              id: companyId,
              name: '', // We don't have company name in JWT, but permission system only needs type
              type: companyType
            } : null
          }
        };

        if (user.email !== null && user.email !== undefined) {
          contextData.email = user.email;
        }
        if (companyId) {
          contextData.company_id = companyId;
        }
        if (payload['is_system_admin'] !== undefined) {
          contextData.is_system_admin = payload['is_system_admin'] === true;
        }

        // DEBUG: Log final UserContext
        systemLogger.debug('[JWT-DEBUG] Final UserContext from JWT:', {
          userId: contextData.id,
          app_role: contextData.app_role,
          is_system_admin: contextData.is_system_admin,
          company_id: contextData.company_id,
          company_type_root: contextData.company_type,  // Root level (permission checks use this)
          company_type_nested: contextData.profile?.company?.type  // Nested (backward compat)
        });

        return contextData;
      } else {
        // Fallback to user metadata if no session
        systemLogger.debug('[JWT-DEBUG] No session access_token, using user_metadata fallback:', {
          userId: user.id,
          email: user.email,
          userMetadataKeys: Object.keys(user.user_metadata || {}),
          app_role: user.user_metadata?.['app_role'],
          company_id: user.user_metadata?.['company_id'],
          company_type: user.user_metadata?.['company_type'],
          is_system_admin: user.user_metadata?.['is_system_admin']
        });

        const appRole = mapAppRole((user.user_metadata?.['app_role'] as string) || 'USER');
        const companyId = user.user_metadata?.['company_id'] as string | undefined;
        const companyType = user.user_metadata?.['company_type'] as 'CLIENT' | 'FACILITATOR' | 'WORKSHOP' | undefined;

        const contextData: UserContext = {
          id: user.id,
          app_role: appRole,
          company_type: companyType || null,  // ADD: Root level for permission checks
          profile: {
            role: appRole,
            company_id: companyId || null,
            company: companyId && companyType ? {
              id: companyId,
              name: '', // We don't have company name in user_metadata, but permission system only needs type
              type: companyType
            } : null
          }
        };

        if (user.email !== null && user.email !== undefined) {
          contextData.email = user.email;
        }
        if (companyId) {
          contextData.company_id = companyId;
        }
        if (user.user_metadata?.['is_system_admin'] !== undefined) {
          contextData.is_system_admin = user.user_metadata['is_system_admin'] === true;
        }

        // DEBUG: Log final UserContext from user_metadata fallback
        systemLogger.debug('[JWT-DEBUG] Final UserContext from user_metadata fallback:', {
          userId: contextData.id,
          app_role: contextData.app_role,
          is_system_admin: contextData.is_system_admin,
          company_id: contextData.company_id,
          company_type_root: contextData.company_type,  // Root level (permission checks use this)
          company_type_nested: contextData.profile?.company?.type  // Nested (backward compat)
        });

        return contextData;
      }
    } catch (_error) {
      componentLogger.error('Failed operation:', 'jwt-decode-error', _error);
      // Fallback to basic user info
      const contextData: UserContext = {
        id: user.id,
        app_role: UserRole.USER,
        profile: {
          role: UserRole.USER,
          company_id: null,
          company: null
        }
      };
      if (user.email !== null && user.email !== undefined) {
        contextData.email = user.email;
      }
      return contextData;
    }
  }, [user, session]);
};